// ---------------------------------------------------------------------------
// SharedSignalsService — HTTP-backed implementation against WorkflowSvc.
//
// Talks to /SharedSignals on the configured API base URL. Response shapes
// mirror the C# DTOs in
// src/services/WorkflowSvc/Api/Contracts/SharedSignals (camelCase JSON
// via ASP.NET Core defaults).
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import { AppError } from '../api/errors.js';
import type {
  EvalAssessment,
  ResponseQuality,
  SharedSignalResponse,
  ShareSignalResult,
} from '../types/sharedSignal.js';

const ROUTES = {
  share: 'tools/SharedSignals/share',
  list: 'tools/SharedSignals/me',
  listAll: 'tools/SharedSignals/all',
  accessCheck: 'tools/SharedSignals/access-check',
  unshare: (signalId: string) =>
    `tools/SharedSignals/${encodeURIComponent(signalId)}`,
  payload: (signalId: string) =>
    `tools/SharedSignals/payload/${encodeURIComponent(signalId)}`,
} as const;

/**
 * Shares a signal by uploading its payload to blob storage.
 * The API fetches the full signal from Substrate before storing it.
 */
export async function shareSignal(
  client: IHttpClient,
  signalId: string,
  substrateAuthHeader?: string,
  itemId?: string,
  description?: string,
  agentName?: string,
  promptPreview?: string,
  responseQuality?: ResponseQuality,
  evalAssessment?: EvalAssessment,
): Promise<ShareSignalResult> {
  const headers: Record<string, string> = {};
  if (substrateAuthHeader) {
    headers['X-Substrate-Authorization'] = substrateAuthHeader;
  }

  const { data } = await client.post<ShareSignalResult>(
    ROUTES.share,
    {
      signalId,
      itemId,
      description,
      agentName,
      promptPreview,
      responseQuality,
      evalAssessment,
    },
    { headers },
  );
  return data;
}

/**
 * Lists all signals the current user has shared.
 */
export async function listSharedSignals(
  client: IHttpClient,
): Promise<SharedSignalResponse[]> {
  const { data } = await client.get<SharedSignalResponse[]>(ROUTES.list);
  return data;
}

/**
 * Unshares (deletes) a previously shared signal.
 * @returns `true` if the signal was deleted, `false` if not found.
 */
export async function unshareSignal(
  client: IHttpClient,
  signalId: string,
): Promise<boolean> {
  try {
    await client.delete(ROUTES.unshare(signalId));
    return true;
  } catch (err) {
    if (err instanceof AppError && err.statusCode === 404) {
      return false;
    }
    throw err;
  }
}

/**
 * Fetches the full signal payload from blob storage for a shared signal.
 * Returns the raw signal JSON string.
 */
export async function getSharedSignalPayload(
  client: IHttpClient,
  signalId: string,
): Promise<string> {
  const { data } = await client.getText(ROUTES.payload(signalId));
  return data;
}

/**
 * Response from the access-check endpoint.
 */
export interface AccessCheckResponse {
  hasAccess: boolean;
  userOid: string;
}

/**
 * Checks whether the current user has the eligibility to view all shared signals.
 * Also returns the user's OID for ownership comparisons.
 */
export async function checkSharedSignalsAccess(
  client: IHttpClient,
): Promise<AccessCheckResponse> {
  const { data } = await client.get<AccessCheckResponse>(ROUTES.accessCheck);
  return data;
}

/**
 * Lists all shared signals across all users (requires eligibility).
 */
export async function listAllSharedSignals(
  client: IHttpClient,
): Promise<SharedSignalResponse[]> {
  const { data } = await client.get<SharedSignalResponse[]>(ROUTES.listAll);
  return data;
}
